/**
 * Time Machine #114 #120 — pure helper that gates the four parking-context
 * exports (parkedJobs, parkJob, unparkJob, isJobParked) to inert versions
 * during time-travel.
 *
 * Plain English:
 *   - Parking is a client-side concept (React state + browser localStorage).
 *     It is NOT tracked in EventLog or AuditSnapshots.
 *   - Time Machine promises a snapshot of historical state. Since parking is
 *     not in the snapshot, the historically-correct answer for "what's
 *     parked at time T" is structurally `[]`.
 *   - This helper enforces that contract at the ScheduleContext boundary:
 *     when `isTimeTraveling` is true, consumers see `parkedJobs: []`,
 *     `isJobParked: () => false`, and `parkJob`/`unparkJob` are no-op.
 *   - The user's live `parkedJobs` state and localStorage are UNTOUCHED —
 *     this helper only swaps the surface that consumers read. Exiting
 *     time-travel restores the user's parked-jobs view exactly as it was.
 *
 * Extracted as a pure utility per CLAUDE.md "extract critical-path logic":
 *   - Silent-failure-prone (parking-state coherence + data-loss risk via
 *     the auto-cleanup useEffect that lives separately in schedule.context.js
 *     and is also gated on `!isTimeTraveling`)
 *   - Tested → extract
 *   - Used at the context boundary, the single read-surface for all
 *     downstream parking consumers (UnassignedProjects, JobTile,
 *     scheduleScreen, optimizerScreen)
 *
 * @param {object} args
 * @param {boolean} args.isTimeTraveling — current time-travel mode flag
 * @param {Array} args.parkedJobs — the live parked-jobs state (from useState)
 * @param {Function} args.parkJob — the live parkJob mutator
 * @param {Function} args.unparkJob — the live unparkJob mutator
 * @param {Function} args.isJobParked — the live isJobParked predicate
 * @returns {{
 *   parkedJobs: Array,
 *   parkJob: Function,
 *   unparkJob: Function,
 *   isJobParked: Function,
 * }}
 */
export function gateParkingForTimeTravel({
  isTimeTraveling,
  parkedJobs,
  parkJob,
  unparkJob,
  isJobParked,
}) {
  if (!isTimeTraveling) {
    return { parkedJobs, parkJob, unparkJob, isJobParked };
  }
  return {
    parkedJobs: [],
    isJobParked: () => false,
    parkJob: () => {
      // eslint-disable-next-line no-console
      console.warn(
        "%cScheduleContext parkJob blocked: cannot park while time-traveling (#120)",
        "color: orange; font-weight: bold;"
      );
    },
    unparkJob: () => {
      // eslint-disable-next-line no-console
      console.warn(
        "%cScheduleContext unparkJob blocked: cannot unpark while time-traveling (#120)",
        "color: orange; font-weight: bold;"
      );
    },
  };
}
